import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Search, Filter, Power, UserCog, PenLine } from 'lucide-react'
import { toast } from 'sonner'

import { users, type AdminUserListItem } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Select } from '../components/Input'
import { RoleBadge } from '../components/StatusBadge'
import { formatDate, formatPhone } from '../lib/format'

type RoleFilter = '' | 'customer' | 'admin' | 'author'

export function UsersPage() {
  const [data, setData] = useState<AdminUserListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [role, setRole] = useState<RoleFilter>('')

  function reload() {
    setLoading(true)
    users
      .list({
        search: search || undefined,
        // The list endpoint accepts the same string set as the role
        // filter — server normalizes the empty string to "no filter".
        role: (role || undefined) as 'customer' | 'admin' | 'author' | undefined,
      })
      .then((r) => setData(r.users))
      .catch((err) => toast.error('Failed to load users', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [search, role])

  async function toggleActive(u: AdminUserListItem) {
    try {
      await users.update(u.id, { isActive: !u.isActive })
      toast.success(u.isActive ? 'Deactivated' : 'Activated')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<AdminUserListItem>[] = [
    {
      header: 'User',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.original.name}</p>
          <p className="text-xs text-slatey">{row.original.email ?? '—'}</p>
        </div>
      ),
    },
    { header: 'Phone', accessorKey: 'phone', cell: ({ getValue }) => formatPhone(String(getValue())) },
    {
      header: 'Role',
      accessorKey: 'role',
      cell: ({ getValue }) => <RoleBadge role={getValue<'customer' | 'admin' | 'author'>()} />,
    },
    {
      header: 'Status',
      accessorKey: 'isActive',
      cell: ({ getValue }) => (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            getValue<boolean>() ? 'bg-forest/10 text-forest' : 'bg-sand text-slatey'
          }`}
        >
          {getValue<boolean>() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { header: 'Orders', accessorKey: 'orderCount' },
    { header: 'Joined', accessorKey: 'createdAt', cell: ({ getValue }) => formatDate(String(getValue())) },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          {/* Show the author-profile link for anyone who can author
              content — full admins or users with role='author'. */}
          {(row.original.role === 'author' || row.original.role === 'admin') && (
            <Link to={`/users/${row.original.id}/author`} title="Author profile">
              <Button variant="ghost" size="icon">
                <PenLine className="size-4 text-ink" />
              </Button>
            </Link>
          )}
          <RoleDialog user={row.original} onChanged={reload} />
          <Button variant="ghost" size="icon" onClick={() => toggleActive(row.original)} title={row.original.isActive ? 'Deactivate' : 'Activate'}>
            <Power className={`size-4 ${row.original.isActive ? 'text-amber' : 'text-forest'}`} />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setSearch(searchInput.trim())
          }}
          className="flex items-center gap-2 flex-1 min-w-[260px]"
        >
          <div className="relative flex-1">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
            <Input
              placeholder="Search by name, phone, or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
        </form>
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-slatey" />
          <Select
            value={role}
            onChange={(e) => setRole((e.target.value || '') as RoleFilter)}
            className="min-w-[140px]"
          >
            <option value="">All roles</option>
            <option value="customer">Customer</option>
            <option value="admin">Admin</option>
            <option value="author">Author</option>
          </Select>
        </div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyTitle="No users match"
        emptyDescription="Try clearing your filters."
      />
    </div>
  )
}

function RoleDialog({
  user,
  onChanged,
}: {
  user: AdminUserListItem
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [role, setRole] = useState<'customer' | 'admin' | 'author'>(user.role)
  const [saving, setSaving] = useState(false)

  async function apply() {
    setSaving(true)
    try {
      await users.update(user.id, { role })
      toast.success('Role updated')
      setOpen(false)
      onChanged()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Change role">
          <UserCog className="size-4 text-ink" />
        </Button>
      </DialogTrigger>
      <DialogContent title={`Change role for ${user.name}`}>
        <FormField label="Role" required>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as 'customer' | 'admin' | 'author')}
          >
            <option value="customer">Customer</option>
            <option value="admin">Admin</option>
            <option value="author">Author</option>
          </Select>
        </FormField>
        <div className="mt-5 flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={apply} loading={saving} size="sm">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}